import React from "react";
import "./App.css";

export default function App() {
  return (
    <div className="min-h-screen bg-lightStone text-navySlate font-sans">

      {/* HEADER */}
      <header className="bg-lightStone border-b border-gray-200 px-4 py-3">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <a href="#top" className="no-underline flex-shrink-0">
            <img
              src="logo.png"
              alt="Jola & Co. logo"
              className="h-12 w-auto object-contain"
            />
          </a>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-5">
            <a href="#what-i-do" className="text-navySlate hover:text-goldenWheat text-sm">What I do</a>
            <a href="#projects" className="text-navySlate hover:text-goldenWheat text-sm">Projects</a>
            <a href="/portfolio.html" className="text-navySlate hover:text-goldenWheat text-sm">Portfolio</a>
            <a href="#certifications" className="text-navySlate hover:text-goldenWheat text-sm">Certifications</a>
            <a href="#about" className="text-navySlate hover:text-goldenWheat text-sm">About</a>
            <a href="#contact" className="text-navySlate hover:text-goldenWheat text-sm">Contact</a>
            <a href="Jola-Tacij-CV.pdf" target="_blank" rel="noopener noreferrer"
               className="bg-goldenWheat text-white px-4 py-2 rounded-lg hover:bg-sage transition text-sm">
              View CV
            </a>
          </nav>
          {/* Mobile: just show CV button */}
          <a href="Jola-Tacij-CV.pdf" target="_blank" rel="noopener noreferrer"
             className="md:hidden bg-goldenWheat text-white px-3 py-2 rounded-lg text-sm font-semibold">
            View CV
          </a>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="py-16 px-4 bg-lightStone text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-goldenWheat text-sm font-semibold tracking-widest uppercase mb-3">Business Analyst · Agile Delivery</p>
          <h2 className="text-4xl font-semibold mb-4">
            I specify solutions that can actually be built — because I'm the one who builds them.
          </h2>
          <p className="text-lg font-medium text-goldenWheat mb-6">
            Requirements &amp; traceability · BPMN 2.0 · UML · Jira · Confluence · Power BI · SQL
          </p>
          <p className="text-lg text-navySlate mb-6">
            Six years in energy and EV infrastructure across 8+ European markets, in operations and BI
            roles where I ran the requirements sessions, mapped the processes and built the reporting
            behind them — the business analysis, whatever the job title said. Over the last two years
            I've formalised that practice: AgilePM® Practitioner, BPMN 2.0 and UML.
          </p>
          <p className="text-lg text-navySlate mb-6">
            <strong>Transition Monitor</strong> is the whole method in one place — 20 business requirements
            traced through 43 functional requirements to a Jira backlog, AS-IS and TO-BE process models,
            a governed star schema and a decision log. The documentation is public, so you don't have to
            take my word for any of it.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <a href="/transition-monitor.html" className="bg-goldenWheat text-lightStone px-6 py-3 rounded-xl shadow hover:bg-sage transition">See the work</a>
            <a href="Jola-Tacij-CV.pdf" target="_blank" rel="noopener noreferrer" className="border border-goldenWheat text-goldenWheat px-6 py-3 rounded-xl hover:bg-goldenWheat hover:text-lightStone transition">Download CV</a>
          </div>
        </div>
      </section>

      {/* WHAT I DO */}
      <section id="what-i-do" className="py-16 px-4 bg-lightStone">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 text-center">What I do</h2>
          <p className="text-center text-navySlate max-w-2xl mx-auto mb-12">
            The analysis that happens before anyone writes code — and the reporting that comes after it.
            Most people do one or the other.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-2">Requirements &amp; traceability</h3>
              <p className="text-navySlate text-sm leading-relaxed">Gather and reconcile conflicting requirements across teams and markets. Numbered business, functional and non-functional requirements, each traceable to a design, a backlog story and a test — so a year later someone can ask why the system does what it does, and get an answer.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-2">Process &amp; systems analysis</h3>
              <p className="text-navySlate text-sm leading-relaxed">AS-IS and TO-BE process design in BPMN 2.0, system behaviour in UML, modelled in Enterprise Architect — so a process map becomes a specification a delivery team can actually build from.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-2">Agile delivery</h3>
              <p className="text-navySlate text-sm leading-relaxed">AgilePM® Practitioner. In the AgilePM/DSDM team model the Business Analyst is a defined role, bridging business and technical interests. Epics and stories in Jira, documentation and decision logs in Confluence, delivery across multi-country programmes.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-2">BI, data modelling &amp; quality</h3>
              <p className="text-navySlate text-sm leading-relaxed">Advanced SQL, DAX and Power Query. Power BI built backwards from the decision, dimensional / star-schema modelling on Azure Databricks, with automated integrity checks built in so the numbers hold up. Currently extending this into Python (pandas), with daily practice.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="projects" className="py-16 px-4 bg-lightStone">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-3 text-center">Case Studies</h2>
          <p className="text-center text-navySlate mb-8">How I've solved real problems for energy, infrastructure, and operations teams.</p>

          {/* Featured case study — links to the full portfolio */}
          <a href="/transition-monitor.html"
             className="block border border-goldenWheat bg-sandBeige rounded-xl p-6 mb-12 hover:shadow-lg transition">
            <span className="text-xs font-bold text-goldenWheat uppercase tracking-widest">Featured case study</span>
            <h3 className="text-xl font-semibold mt-2 mb-2">
              Transition Monitor — a 15-day manual scorecard, redesigned as a governed process
            </h3>
            <p className="text-sm text-navySlate opacity-80 mb-3">
              Business analysis end to end: AS-IS and TO-BE process models in BPMN 2.0, a star schema that
              makes every published number traceable, and wireframes annotated with their requirement IDs.
            </p>
            <span className="text-sm font-semibold text-sage">Read the full case study →</span>
          </a>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <a href="/portfolio.html#work" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <span className="text-xs font-bold text-sage uppercase tracking-wide">AI-Assisted Development · Claude Code</span>
              <h3 className="text-lg font-semibold mt-2 mb-2">The Chaos Inbox — from 6 hours of Friday admin to seconds</h3>
              <p className="text-sm text-navySlate leading-relaxed">149 supplier documents in, 938 clean order lines out — in seconds. A five-stage Python pipeline that catches mis-keyed orders and silent price rises. Built on fully synthetic data; the client is fictional, the pipeline is not.</p>
            </a>
            <a href="/portfolio.html#work" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <span className="text-xs font-bold text-sage uppercase tracking-wide">E.ON Drive · Energy Infrastructure</span>
              <h3 className="text-lg font-semibold mt-2 mb-2">EV Utilization Model — 10 Countries, Zero Hardcoding</h3>
              <p className="text-sm text-navySlate leading-relaxed">A hardcoded spreadsheet rebuilt as a governed 10-country model — SQL transformation layer on Azure Databricks, star schema and DAX, with automated data-integrity checks and live Power BI reporting for senior leadership.</p>
            </a>
            <a href="/portfolio.html#work" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <span className="text-xs font-bold text-sage uppercase tracking-wide">E.ON Drive · Site Selection</span>
              <h3 className="text-lg font-semibold mt-2 mb-2">Geospatial Site Scoring Dashboard — 890 Sites Evaluated</h3>
              <p className="text-sm text-navySlate leading-relaxed">Geospatial Power BI models scoring 890+ sites, integrating procurement, finance and project data into a single location strategy that senior management could read and act on.</p>
            </a>
            <a href="/portfolio.html#work" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <span className="text-xs font-bold text-sage uppercase tracking-wide">SWARCO · Infrastructure</span>
              <h3 className="text-lg font-semibold mt-2 mb-2">EV Investment Department — Data Foundation from Zero</h3>
              <p className="text-sm text-navySlate leading-relaxed">A department reporting function co-built from nothing — SQL pipelines and analytical models, recurring reporting automated, and the analysis presented to the Board.</p>
            </a>
          </div>

          <p className="text-sm text-navySlate text-center mb-8">
            Beyond one-off projects, I've also delivered: E.ON Competitor Intelligence Platform · DNO Process Standardisation · SWARCO Scotland Operations Analysis
          </p>

          <p className="text-center">
            <a href="/portfolio.html" className="inline-block border border-goldenWheat text-goldenWheat px-6 py-3 rounded-xl hover:bg-goldenWheat hover:text-lightStone transition">
              See all case studies →
            </a>
          </p>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section id="certifications" className="py-16 px-4 bg-lightStone">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-10 text-center">Certifications & Professional Development</h2>

          <div className="grid gap-10 md:grid-cols-3 mb-12">

            <div className="text-center">
              <div className="h-40 w-40 mx-auto mb-5 flex items-center justify-center">
                <img src="agilepm-practitioner-badge.png" alt="AgilePM v3 Practitioner" className="h-full w-full object-contain" />
              </div>
              <h3 className="text-lg font-bold mb-2">AgilePM® v3 Practitioner</h3>
              <p className="text-sm text-navySlate mb-3">
                Proven methodology for managing iterative delivery, cross-functional teams,
                and complex stakeholder environments.
              </p>
              <p className="text-sm font-semibold text-goldenWheat">APMG International</p>
            </div>

            <div className="text-center">
              <div className="h-40 w-40 mx-auto mb-5 flex items-center justify-center">
                <img src="cert-PL-300-Power-BI-Data-Analyst.png" alt="Power BI PL-300" className="h-full w-full object-contain" />
              </div>
              <h3 className="text-lg font-bold mb-2">Power BI Data Analyst (PL-300)</h3>
              <p className="text-sm text-navySlate mb-3">
                Microsoft exam passed 2023. Data modelling, visualisation and BI — core to every
                dashboard and reporting engagement. Currently studying for DP-600, Fabric Analytics Engineer.
              </p>
              <p className="text-sm font-semibold text-goldenWheat">Microsoft · 2023–2024</p>
            </div>

            <div className="text-center">
              <div className="h-40 w-40 mx-auto mb-5 flex items-center justify-center">
                <img src="cert-ai-mastermind.png" alt="Claude & AI Mastermind INTL" className="h-full w-full object-contain" />
              </div>
              <h3 className="text-lg font-bold mb-2">Claude & AI Mastermind INTL</h3>
              <p className="text-sm text-navySlate mb-3">
                Prompt engineering, context engineering, AI agent creation, and workflow
                automation — directly shaping how I deliver faster and smarter on every project.
              </p>
              <p className="text-sm font-semibold text-goldenWheat">Outskill, 2026</p>
            </div>

          </div>

          <div>
            <h3 className="text-xl font-bold mb-6 text-center">Technical Training & Specialized Skills</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border-l-4 border-goldenWheat pl-4">
                <p className="font-semibold text-navySlate">15 Days of SQL: The Complete SQL Masterclass 2024</p>
                <p className="text-sm text-navySlate opacity-70">Udemy · Nikolai Schuler · Jan 2024</p>
              </div>
              <div className="border-l-4 border-goldenWheat pl-4">
                <p className="font-semibold text-navySlate">SQL - MySQL for Data Analytics & BI</p>
                <p className="text-sm text-navySlate opacity-70">Udemy · 365 Careers · Aug 2023</p>
              </div>
              <div className="border-l-4 border-sage bg-sandBeige rounded-r-lg pl-4 pr-4 py-3">
                <span className="inline-block bg-sage text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-1 uppercase tracking-wide">★ Latest</span>
                <p className="font-semibold text-navySlate">Make — from Beginner to Pro: AI Automations &amp; AI Agents</p>
                <p className="text-sm text-navySlate opacity-70">Udemy · Thalita Milan · 2026</p>
              </div>
              <div className="border-l-4 border-goldenWheat pl-4">
                <p className="font-semibold text-navySlate">Business Analysis Fundamentals - IIBA Endorsed</p>
                <p className="text-sm text-navySlate opacity-70">Udemy · The BA Guide · May 2025</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-16 px-4 bg-lightStone">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">About Me</h2>
          <img
            src="jola-and-dogs.png"
            alt="Jola with her two rescue dogs in Abruzzo"
            className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 rounded-full object-cover shadow-md"
          />
          <p className="text-navySlate leading-relaxed mb-4">
            I listen first, and then I ask the questions people find slightly annoying — what does
            "complete" actually mean here, who signs this off, what happens when the data is missing.
            Ambiguity that survives a workshop turns into a defect later, so I would rather find it
            early. I don't hide behind jargon: if I can't explain a model to the person who has to
            use it, it isn't finished.
          </p>
          <p className="text-navySlate leading-relaxed mb-4">
            I document as I go, so whoever comes after me can see what was decided and why. And I
            train the people who inherit the work — data engineers, country managers, a technical PM,
            a CFO. Reporting that nobody trusts or knows how to open hasn't been delivered, it has
            just been built.
          </p>
          <p className="text-navySlate leading-relaxed text-sm opacity-80">
            Polish is my first language and I work in English every day. Based in Abruzzo, Italy —
            CET, fully remote, and used to teams spread across eight countries and as many opinions.
            Here with my family and our two rescue dogs.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-16 px-4 bg-lightStone">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Get in touch</h2>
          <p className="mb-8 text-navySlate">
            Open to Business Analyst and BA/BI roles across Europe — remote or hybrid — and to project work through Jola &amp; Co. Email is the fastest route.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <a href="mailto:jola@jolaandco.com" className="bg-goldenWheat text-lightStone px-6 py-3 rounded-xl shadow hover:bg-sage transition">Email Me</a>
            <a href="Jola-Tacij-CV.pdf" target="_blank" rel="noopener noreferrer" className="border border-goldenWheat text-goldenWheat px-6 py-3 rounded-xl hover:bg-goldenWheat hover:text-lightStone transition">View CV (PDF)</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navySlate text-lightStone text-center py-6 px-4 text-sm">
        <p className="text-goldenWheat tracking-widest uppercase text-xs mb-2">Business Analysis · Agile Delivery · BI</p>
        <p>© {new Date().getFullYear()} Jola Tacij · <a href="mailto:jola@jolaandco.com" className="underline hover:text-goldenWheat">jola@jolaandco.com</a></p>
        <p className="mt-1 text-gray-400">Requirements &amp; Traceability · BPMN 2.0 · UML · Jira · Confluence · Power BI · SQL · Python (pandas) · Remote</p>
        <p className="mt-3 text-xs text-gray-400"><a href="/services.html" className="hover:text-goldenWheat">Consulting services</a></p>
      </footer>

    </div>
  );
}
